/* eslint-disable react/jsx-no-useless-fragment */
import DateFormatter from 'components/DateFormatter';
import UserInfo from 'components/UserInfo';
import * as Yup from 'yup';
import { Badge } from 'reactstrap';
import { currencyToLocal, NumberWithCommasFormat } from 'utils/constants';

/* eslint-disable react/react-in-jsx-scope */

const DEFAULT_PHONE_MAX_LENGTH = 10;

const PHONE_LENGTH_BY_CALLING_CODE = {
  '1': 10,
  '27': 9,
  '33': 9,
  '44': 10,
  '49': 11,
  '52': 10,
  '55': 11,
  '61': 9,
  '62': 11,
  '63': 10,
  '64': 9,
  '65': 8,
  '66': 9,
  '81': 10,
  '82': 10,
  '84': 9,
  '86': 11,
  '90': 10,
  '91': 10,
  '92': 10,
  '94': 9,
  '95': 10,
  '98': 10,
  '212': 9,
  '213': 9,
  '216': 8,
  '218': 9,
  '221': 9,
  '234': 10,
  '351': 9,
  '352': 9,
  '353': 9,
  '358': 9,
  '420': 9,
  '421': 9,
  '852': 8,
  '855': 9,
  '856': 9,
  '880': 10,
  '886': 9,
  '966': 9,
  '971': 9,
  '972': 9,
  '973': 8,
  '974': 8,
  '975': 8,
  '976': 8,
  '977': 10,
  '993': 8,
  '994': 9,
  '995': 9,
  '996': 9,
  '998': 9,
};

export const getPhoneNumberMaxLength = (callingCode) => {
  const rawCallingCode =
    callingCode?.callingCode ?? callingCode?.id ?? callingCode ?? '';
  const numericCallingCode = String(rawCallingCode).replace(/\D/g, '');

  if (PHONE_LENGTH_BY_CALLING_CODE[numericCallingCode]) {
    return PHONE_LENGTH_BY_CALLING_CODE[numericCallingCode];
  }

  if (!numericCallingCode) {
    return DEFAULT_PHONE_MAX_LENGTH;
  }

  return Math.max(DEFAULT_PHONE_MAX_LENGTH, 15 - numericCallingCode.length);
};

export const subscriptionQuantity =
  'Subscription Quantity Updated Successfully';

export const vendorBidsColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },
  { accessorKey: 'latestBid.bidType', header: 'Type', enableSorting: true },
  {
    accessorFn: (row) => {
      return row?.latestBid?.bidType === 'OFFER' ? (
        <UserInfo
          name={row?.latestBid?.offer?.trader?.entityName}
          mobileNumber={`${row?.latestBid?.offer?.trader?.callingCode?.callingCode}-${row?.latestBid?.offer?.trader?.phone}`}
        />
      ) : (
        <UserInfo
          name={row?.latestBid?.demand?.trader?.entityName}
          mobileNumber={`${row?.latestBid?.demand?.trader?.callingCode?.callingCode}-${row?.latestBid?.demand?.trader?.phone}`}
        />
      );
    },
    header: 'seller',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      return row?.latestBid?.bidType === 'OFFER' ? (
        <UserInfo
          name={row?.latestBid?.bidder?.entityName}
          mobileNumber={`${row?.latestBid?.bidder?.callingCode?.callingCode}-${row?.latestBid?.bidder?.phone}`}
        />
      ) : (
        <UserInfo
          name={row?.latestBid?.bidder?.entityName}
          mobileNumber={`${row?.latestBid?.bidder?.callingCode?.callingCode}-${row?.latestBid?.bidder?.phone}`}
        />
      );
    },
    header: 'Last Bidder',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      return row?.latestBid?.bidType === 'OFFER'
        ? row?.latestBid?.offer?.grades.map((item) => {
            return (
              <Badge key={item.id} className="mb-1 me-1" color="primary">
                {item.name}
              </Badge>
            );
          })
        : row?.latestBid?.demand?.grades.map((item) => {
            return (
              <Badge key={item.id} className="mb-1 me-1" color="primary">
                {item.name}
              </Badge>
            );
          });
    },
    header: 'grade',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      return row?.latestBid?.bidType === 'OFFER' ? (
        <>
          <div className="mb-1 text-nowrap">
            {row?.latestBid?.offer?.city?.cityName
              ? row?.latestBid?.offer?.city?.cityName
              : row?.latestBid?.offer?.portsOfDischarge?.map(
                  (portOfDischarge) => {
                    return (
                      <div key={portOfDischarge?.id} className="me-1">
                        {`${portOfDischarge?.name},` || 'Not Available'}
                      </div>
                    );
                  },
                )}
          </div>
          <div className="font-manrope text-active">
            {row?.latestBid?.offer?.state?.stateName}
          </div>
        </>
      ) : (
        <>
          <div className="mb-1 text-nowrap">
            {row?.latestBid?.demand?.city?.cityName
              ? row?.latestBid?.demand?.city?.cityName
              : row?.latestBid?.demand?.portsOfDischarge?.map(
                  (portOfDischarge) => {
                    return (
                      <div key={portOfDischarge?.id} className="me-1">
                        {`${portOfDischarge?.name},` || 'Not Available'}
                      </div>
                    );
                  },
                )}
          </div>
          <div className="font-manrope text-active">
            {row?.latestBid?.demand?.state?.stateName}
          </div>
        </>
      );
    },
    header: 'Location',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      const sourceData =
        row?.latestBid?.bidType === 'OFFER'
          ? row?.latestBid?.offer
          : row?.latestBid?.demand;
      const qtyUnit = sourceData?.quantityUnit || '';
      const actualUnit = sourceData?.actualQuantityUnit || qtyUnit;
      return row?.latestBid?.bidType === 'OFFER'
        ? `${NumberWithCommasFormat(row?.latestBid?.offer?.totalQty)} ${actualUnit}`
        : `${NumberWithCommasFormat(row?.latestBid?.demand?.totalQty)} ${actualUnit}`;
    },
    header: 'total QTY',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      const sourceData =
        row?.latestBid?.bidType === 'OFFER'
          ? row?.latestBid?.offer
          : row?.latestBid?.demand;
      const qtyUnit = sourceData?.quantityUnit || '';
      const actualUnit = sourceData?.actualQuantityUnit || qtyUnit;
      return row?.latestBid?.bidType === 'OFFER'
        ? `${NumberWithCommasFormat(row?.latestBid?.offer?.minimumQty)} ${actualUnit}`
        : `${NumberWithCommasFormat(row?.latestBid?.demand?.minimumQty)} ${actualUnit}`;
    },
    header: 'min. QTY',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },

  {
    accessorFn: (row) => {
      return (
        <div className="px-1 text-nowrap">
          {row?.latestBid?.bidType === 'OFFER'
            ? ` ${currencyToLocal(
                row?.latestBid?.offer?.rate,
                row?.latestBid?.rateUnit?.isoCurrencyCode,
              )}/${row?.latestBid?.offer?.quantityUnit}`
            : `${currencyToLocal(
                row?.latestBid?.demand?.rate,
                row?.latestBid?.rateUnit?.isoCurrencyCode,
              )}/${row?.latestBid?.demand?.quantityUnit}`}
        </div>
      );
    },
    header: 'rate',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  // { accessorKey: 'latestBid.id', header: 'Bid ID', enableSorting: true },
  // { accessorKey: 'latestBid.bidderRole', header: 'Role', enableSorting: true },
  {
    accessorFn: (row) => {
      const sourceData =
        row?.latestBid?.bidType === 'OFFER'
          ? row?.latestBid?.offer
          : row?.latestBid?.demand;
      const qtyUnit =
        sourceData?.quantityUnit || row?.latestBid?.quantityUnit || '';
      return (
        <div className="px-1 text-nowrap">
          {`${currencyToLocal(
            row?.latestBid?.price,
            row?.latestBid?.rateUnit?.isoCurrencyCode,
          )}/${qtyUnit}`}
        </div>
      );
    },
    cell: (info) => info.getValue(),
    header: 'Bid Rate',
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      const sourceData =
        row?.latestBid?.bidType === 'OFFER'
          ? row?.latestBid?.offer
          : row?.latestBid?.demand;
      const qtyUnit =
        sourceData?.quantityUnit || row?.latestBid?.quantityUnit || '';
      return (
        <div className="px-1">
          {NumberWithCommasFormat(row?.latestBid?.quantity)} {qtyUnit}{' '}
        </div>
      );
    },
    cell: (info) => info.getValue(),
    header: 'bid Quantity',
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      return <div className="px-1">{row?.latestBid?.status}</div>;
    },
    cell: (info) => info.getValue(),
    header: 'STATUS',
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      return <DateFormatter dateTime={row?.latestBid?.bidExpireTime} />;
    },
    cell: (info) => info.getValue(),
    header: 'Bid Expire time',
    enableSorting: true,
  },
];

export const vendorChildBidsColumns = [
  // { accessorKey: 'bidType', header: 'Type', enableSorting: true },
  {
    accessorFn: (row) => {
      return row?.bidType === 'OFFER' ? (
        <UserInfo
          name={row?.offer?.trader?.entityName}
          mobileNumber={`${row?.offer?.trader?.callingCode?.callingCode}-${row?.offer?.trader?.phone}`}
        />
      ) : (
        <UserInfo
          name={row?.demand?.trader?.entityName}
          mobileNumber={`${row?.demand?.trader?.callingCode?.callingCode}-${row?.demand?.trader?.phone}`}
        />
      );
    },
    header: 'seller',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      return row?.bidType === 'OFFER' ? (
        <UserInfo
          name={row?.bidder?.entityName}
          mobileNumber={`${row?.bidder?.callingCode?.callingCode}-${row?.bidder?.phone}`}
        />
      ) : (
        <UserInfo
          name={row?.bidder?.entityName}
          mobileNumber={`${row?.bidder?.callingCode?.callingCode}-${row?.bidder?.phone}`}
        />
      );
    },
    header: 'Last Bidder',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      return row?.bidType === 'OFFER'
        ? row?.offer?.grades.map((item) => {
            return (
              <Badge key={item.id} className="mb-1 me-1" color="primary">
                {item.name}
              </Badge>
            );
          })
        : row?.demand?.grades.map((item) => {
            return (
              <Badge key={item.id} className="mb-1 me-1" color="primary">
                {item.name}
              </Badge>
            );
          });
    },
    header: 'grade',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      return row?.bidType === 'OFFER' ? (
        <>
          <div className="mb-1 text-nowrap">
            {row?.offer?.city?.cityName
              ? row?.offer?.city?.cityName
              : row?.offer?.portsOfDischarge?.map((portOfDischarge) => {
                  return (
                    <div key={portOfDischarge?.id} className="me-1">
                      {`${portOfDischarge?.name},` || 'Not Available'}
                    </div>
                  );
                })}
          </div>
          <div className="font-manrope text-active">
            {row?.offer?.state?.stateName}
          </div>
        </>
      ) : (
        <>
          <div className="mb-1 text-nowrap">
            {row?.demand?.city?.cityName
              ? row?.demand?.city?.cityName
              : row?.demand?.portsOfDischarge?.map((portOfDischarge) => {
                  return (
                    <div key={portOfDischarge?.id} className="me-1">
                      {`${portOfDischarge?.name},` || 'Not Available'}
                    </div>
                  );
                })}
          </div>
          <div className="font-manrope text-active">
            {row?.demand?.state?.stateName}
          </div>
        </>
      );
    },
    header: 'Location',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      const sourceData = row?.bidType === 'OFFER' ? row?.offer : row?.demand;
      const qtyUnit = sourceData?.quantityUnit || '';
      const actualUnit = sourceData?.actualQuantityUnit || qtyUnit;
      return row?.bidType === 'OFFER'
        ? `${NumberWithCommasFormat(row?.offer?.totalQty)} ${actualUnit}`
        : `${NumberWithCommasFormat(row?.demand?.totalQty)} ${actualUnit}`;
    },
    header: 'total QTY',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  {
    accessorFn: (row) => {
      const sourceData = row?.bidType === 'OFFER' ? row?.offer : row?.demand;
      const qtyUnit = sourceData?.quantityUnit || '';
      const actualUnit = sourceData?.actualQuantityUnit || qtyUnit;
      return row?.bidType === 'OFFER'
        ? `${NumberWithCommasFormat(row?.offer?.minimumQty)} ${actualUnit}`
        : `${NumberWithCommasFormat(row?.demand?.minimumQty)} ${actualUnit}`;
    },
    header: 'min. QTY',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },

  {
    accessorFn: (row) => {
      return (
        <div className="px-1 text-nowrap">
          {row?.bidType === 'OFFER'
            ? `${currencyToLocal(row?.offer?.rate, row?.rateUnit?.isoCurrencyCode)}/${row?.offer?.quantityUnit}`
            : `${currencyToLocal(row?.demand?.rate, row?.rateUnit?.isoCurrencyCode)}/${row?.demand?.quantityUnit}`}
        </div>
      );
    },
    header: 'rate',
    enableSorting: false,
    cell: (info) => info.getValue(),
  },
  // { accessorKey: 'latestBid.id', header: 'Bid ID', enableSorting: true },
  // { accessorKey: 'latestBid.bidderRole', header: 'Role', enableSorting: true },
  {
    accessorFn: (row) => {
      const sourceData = row?.bidType === 'OFFER' ? row?.offer : row?.demand;
      const qtyUnit = sourceData?.quantityUnit || row?.quantityUnit || '';
      return (
        <div className="px-1 text-nowrap">
          {`${currencyToLocal(row?.price, row?.rateUnit?.isoCurrencyCode)}/${qtyUnit}`}
        </div>
      );
    },
    cell: (info) => info.getValue(),
    header: 'Bid Rate',
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      const sourceData = row?.bidType === 'OFFER' ? row?.offer : row?.demand;
      const qtyUnit = sourceData?.quantityUnit || row?.quantityUnit || '';
      return (
        <div className="px-1">
          {NumberWithCommasFormat(row?.quantity)} {qtyUnit}{' '}
        </div>
      );
    },
    cell: (info) => info.getValue(),
    header: 'bid Quantity',
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      return <div className="px-1">{row?.status}</div>;
    },
    cell: (info) => info.getValue(),
    header: 'STATUS',
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      return <DateFormatter dateTime={row?.bidExpireTime} />;
    },
    cell: (info) => info.getValue(),
    header: 'Bid Expire time',
    enableSorting: true,
  },
];

export const vendorOffersColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'trader',
    header: 'Seller',
    enableSorting: false,
    accessorFn: (row) => {
      return (
        <UserInfo
          name={
            row?.trader?.entityName ? row?.trader?.entityName : 'Not Available'
          }
          mobileNumber={`${row?.trader?.callingCode?.callingCode}-${row?.trader?.phone}`}
        />
      );
    },
    cell: (info) => info.getValue(),
  },
  { accessorKey: 'id', header: 'OFFER ID', enableSorting: true },

  {
    accessorKey: 'grade',
    header: 'GRADE',
    cell: ({ row }) =>
      row?.original?.grades?.map((grade) => {
        return (
          <Badge key={grade?.id} color="primary" className="me-1">
            {grade?.name}
          </Badge>
        );
      }),
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.offerType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portsOfDischarge?.map((portOfDischarge) => {
                return (
                  <div key={portOfDischarge?.id} className="me-1">
                    {`${portOfDischarge?.name},` || 'Not Available'}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">
            {(row?.offerType === 'FOREIGN' && row?.portOfDischarge?.name) || ''}
            {row.city?.cityName || ''}
          </div>
          <div className="font-manrope text-active">
            {row.state?.stateName || ''}
          </div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'minimumQty',
    header: 'MIN. QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.minimumQty)}{' '}
        {row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    header: 'RATE',
    // accessorFn: (row) => {
    //   const price = row?.rate;
    //   return <div className="px-1">{`${price}/kg`}</div>;
    // },
    // cell: (info) => info.getValue(),
    id: 'rate',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(row?.original?.rate, row?.original?.rateUnit?.isoCurrencyCode)}/${row?.original?.quantityUnit}`}
      </div>
    ),
    enableSorting: true,
  },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'validityExpireTime',
    header: 'EXPIRY DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityExpireTime} />
    ),
    enableSorting: true,
  },
  // { accessorKey: 'actions', header: '', enableSorting: false },
];

export const vendorDemandsColumns = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },

  {
    accessorKey: 'trader',
    header: 'Buyer',
    enableSorting: false,
    accessorFn: (row) => {
      return (
        <UserInfo
          name={
            row?.trader?.entityName ? row?.trader?.entityName : 'Not Available'
          }
          mobileNumber={`${row?.trader?.callingCode?.callingCode}-${row?.trader?.phone}`}
        />
      );
    },
    cell: (info) => info.getValue(),
  },
  { accessorKey: 'id', header: 'DEMAND ID', enableSorting: true },
  {
    accessorKey: 'grades',
    header: 'GRADE',
    accessorFn: (row) => {
      return row?.grades?.map((grade) => {
        return (
          <Badge key={grade?.id} color="primary" className="me-1">
            {grade?.name}
          </Badge>
        );
      });
    },
    cell: (info) => info.getValue(),
    enableSorting: false,
  },
  {
    accessorFn: (row) => {
      return (
        <>
          {row?.demandType === 'FOREIGN' ? (
            <div className="mb-1">
              {row?.portsOfDischarge?.map((portOfDischarge) => {
                return (
                  <div key={portOfDischarge?.id} className="me-1">
                    {`${portOfDischarge?.name},` || 'Not Available'}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div className="mb-1">
                {row.city?.cityName || 'Not Available'}
              </div>
              <div className="font-manrope text-active">
                {row.state?.stateName || 'Not Available'}
              </div>
            </>
          )}
          {/* <div className="mb-1">{row.city?.cityName}</div>
          <div className="font-manrope text-active">{row.state?.stateName}</div> */}
        </>
      );
    },
    header: 'LOCATION',
    id: 'city.cityName',
    enableSorting: true,
    cell: (info) => info.getValue(),
  },

  {
    accessorKey: 'totalQty',
    header: 'TOTAL QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.totalQty)}{' '}
        {row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'minimumQty',
    // header: 'MOQ',
    header: 'MIN. QTY',
    cell: ({ row }) => (
      <>
        {NumberWithCommasFormat(row?.original?.minimumQty)}{' '}
        {row?.original?.quantityUnit}
      </>
    ),
    enableSorting: true,
  },
  {
    header: 'RATE',
    cell: ({ row }) => (
      <div className="text-nowrap">
        {`${currencyToLocal(row?.original?.rate, row?.original?.rateUnit?.isoCurrencyCode)}/${row?.original?.quantityUnit}`}
      </div>
    ),
    id: 'rate',
    enableSorting: true,
  },
  // { accessorKey: 'demandType', header: 'TYPE', enableSorting: true },
  { accessorKey: 'status', header: 'Status', enableSorting: true },
  {
    accessorKey: 'validityExpireTime',
    header: 'EXPIRY DATE',
    cell: ({ row }) => (
      <DateFormatter dateTime={row?.original?.validityExpireTime} />
    ),
    enableSorting: true,
  },
  // { accessorKey: 'actions', header: '', enableSorting: false },
];

export const additionalUserValidationSchema = Yup.object({
  firstName: Yup.string('Enter the First Name')
    .required('First Name is required')
    .trim(),
  lastName: Yup.string('Enter the Last Name')
    .required('Last Name is required')
    .trim(),
  // middle_name: Yup.string().required('Middle name is required'),
  // isKycVerified: Yup.string().required('Verification status is required'),
  // country: Yup.string().required('Country is required'),
  // city: Yup.string().required('City is required'),
  // state: Yup.string().required('State is required'),
  // phone_number: Yup.number().required('Phone number is required'),
  email: Yup.string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),

  callingCode: Yup.object().required('Please Select Calling code'),
  phone: Yup.string()
    .required('Phone number is required')
    .matches(/^\d+$/, 'Phone number must contain only numbers')
    .when('callingCode', (callingCode, schema) =>
      schema.max(
        getPhoneNumberMaxLength(callingCode),
        `Phone number can be maximum ${getPhoneNumberMaxLength(callingCode)} digits`,
      ),
    )
    .trim(),
  access: Yup.object().required('Please select user access').nullable(),
});

export const vendorPlanValidationSchema = Yup.object().shape({
  subscriptionPlan: Yup.object('')
    .required('Subscription Plan is required')
    .nullable(),
  // mode_of_payment: Yup.object('')
  //   .required('Mode of Payment is required')
  //   .nullable(),
  // offerDemandType: Yup.object('')
  //   .required('Offer/Demand is required')
  //   .nullable(),
});

export const indianVendorKYCSchema = Yup.object().shape({
  panPhoto: Yup.mixed().test(
    'fileType',
    'Only JPG, PNG and PDF files are allowed',
    (value) =>
      value &&
      (value.includes('.png') ||
        value.includes('.jpg') ||
        value.includes('.jpeg') ||
        value.includes('.pdf')),
  ),
  gstPhoto: Yup.mixed().test(
    'fileType',
    'Only JPG, PNG and PDF files are allowed',
    (value) =>
      value &&
      (value.includes('.png') ||
        value.includes('.jpg') ||
        value.includes('.jpeg') ||
        value.includes('.pdf')),
  ),
  aadharBack: Yup.mixed().test(
    'fileType',
    'Only JPG, PNG and PDF files are allowed',
    (value) =>
      value &&
      (value.includes('.png') ||
        value.includes('.jpg') ||
        value.includes('.jpeg') ||
        value.includes('.pdf')),
  ),
  aadharFront: Yup.mixed().test(
    'fileType',
    'Only JPG, PNG and PDF files are allowed',
    (value) =>
      value &&
      (value.includes('.png') ||
        value.includes('.jpg') ||
        value.includes('.jpeg') ||
        value.includes('.pdf')),
  ),
  userVerificationDocument: Yup.mixed().test(
    'fileType',
    'Only PDF file is allowed',
    (value) => value && value.includes('.pdf'),
  ),
});

export const foreignVendorKYCSchema = Yup.object().shape({
  companyFormationDocument: Yup.mixed()
    .nullable()
    .test(
      'fileType',
      'Only JPG, PNG and PDF files are allowed',
      (value) =>
        value &&
        (value.includes('.png') ||
          value.includes('.jpg') ||
          value.includes('.jpeg') ||
          value.includes('.pdf')),
    ),
  foreignIdVerificationDocument: Yup.mixed()
    .nullable()
    .test(
      'fileType',
      'Only JPG, PNG and PDF files are allowed',
      (value) =>
        value &&
        (value.includes('.png') ||
          value.includes('.jpg') ||
          value.includes('.jpeg') ||
          value.includes('.pdf')),
    ),
});

export const getCurrentDateISO = () => {
  const currentDate = new Date();
  return currentDate.toISOString();
};

export const getDaysBetweenDates = (date1, date2) => {
  const msInDay = 24 * 60 * 60 * 1000;
  const date1Obj = new Date(date1);
  const date2Obj = new Date(date2);
  const diffInMs = date2Obj - date1Obj;
  return Math.floor(diffInMs / msInDay);
};
